import { useState, useEffect } from "react";
import { Loader2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/tenantClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Logo } from "@/components/common/Logo";
import { cn } from "@/lib/utils";

interface MFAVerifyFormProps {
  factorId: string;
  onSuccess: () => void;
  onSignOut: () => void;
}

export function MFAVerifyForm({ factorId, onSuccess, onSignOut }: MFAVerifyFormProps) {
  const { toast } = useToast();
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(120); // 2 minutes

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) return;

    setLoading(true);
    try {
      // Create a challenge for the factor
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) {
        throw challengeError;
      }

      // Verify the challenge with the code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verificationCode,
      });

      if (verifyError) {
        throw verifyError;
      }

      toast({
        title: "Welcome back!",
        description: "Successfully signed in.",
        duration: 5000,
      });

      onSuccess();
    } catch (error: any) {
      console.error("MFA verification error:", error);
      toast({
        title: "Verification failed",
        description: "Invalid verification code. Please try again.",
        variant: "destructive",
      });
      setVerificationCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onSignOut();
  };

  // 2-minute countdown timer for MFA entry
  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          toast({
            title: "Session Expired",
            description: "2FA code not entered within the required timeframe.",
            variant: "destructive",
            duration: 5000,
          });
          supabase.auth.signOut().then(() => onSignOut());
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [toast, onSignOut]);

  return (
    <div className="space-y-8">
      {/* Logo and title */}
      <div className="flex flex-col items-center space-y-8">
        <Logo size="lg" />
        <div className="text-center">
          <h1 className="text-2xl font-realhead tracking-tight text-white">Two-Factor Authentication</h1>
          <p className="text-gray-400 mt-2">
            Enter the 6-digit code from your authenticator app
          </p>
        </div>
        <div className="flex items-center justify-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-gray-400" />
          <span className={cn(
            "font-mono",
            remainingSeconds <= 30 ? "text-red-400" : "text-gray-400"
          )}>
            Time remaining: {formatTime(remainingSeconds)}
          </span>
        </div>
      </div>

      {/* OTP and buttons without card */}
      <div className="flex flex-col items-center space-y-6">
        <InputOTP
          maxLength={6}
          value={verificationCode}
          onChange={setVerificationCode}
          onComplete={handleVerify}
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>

        <div className="w-[240px] space-y-2">
          <Button
            onClick={handleVerify}
            disabled={verificationCode.length !== 6 || loading}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify"
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={handleSignOut}
            className="w-full text-gray-400 hover:text-white"
          >
            Sign in with different account
          </Button>
        </div>
      </div>
    </div>
  );
}
